/**
 * Service for rank calculation and rank-related utilities
 *
 * Rank is determined by total spent XP according to the following thresholds:
 * - Rank 1: 5,000-6,999 spent XP (characters with <5000 XP are treated as Rank 1)
 * - Rank 2: 7,000-9,999 spent XP
 * - Rank 3: 10,000-12,999 spent XP
 * - Rank 4: 13,000-16,999 spent XP
 * - Rank 5: 17,000-20,999 spent XP
 * - Rank 6: 21,000-24,999 spent XP
 * - Rank 7: 25,000-29,999 spent XP
 * - Rank 8: 30,000+ spent XP
 */

// Re-export Rank type from advancements for convenience
export type { Rank } from '../types/advancements';
import type { Rank } from '../types/advancements';

/**
 * Interface defining the XP thresholds for each rank
 */
export interface RankThreshold {
  /** The rank number (1-8) */
  rank: Rank;
  /** Minimum spent XP to achieve this rank */
  minXP: number;
  /** Maximum spent XP before advancing to next rank (Infinity for Rank 8) */
  maxXP: number;
}

/**
 * Rank threshold definitions
 *
 * Note: Rank 1 minXP is 0 to handle edge case where characters have
 * spent less than 5000 XP (e.g., from data migration). These characters
 * are treated as Rank 1.
 */
export const RANK_THRESHOLDS: RankThreshold[] = [
  { rank: 1, minXP: 0, maxXP: 6999 },
  { rank: 2, minXP: 7000, maxXP: 9999 },
  { rank: 3, minXP: 10000, maxXP: 12999 },
  { rank: 4, minXP: 13000, maxXP: 16999 },
  { rank: 5, minXP: 17000, maxXP: 20999 },
  { rank: 6, minXP: 21000, maxXP: 24999 },
  { rank: 7, minXP: 25000, maxXP: 29999 },
  { rank: 8, minXP: 30000, maxXP: Infinity }
];

/**
 * Service class for rank-related calculations
 *
 * Functions will be implemented in subsequent tasks:
 * - Task 1.2: calculateRank() ✓
 * - Task 1.3: getXPToNextRank() ✓
 * - Task 1.4: getRankProgress()
 * - Task 1.5: getAvailableRanks()
 */
export class RankService {
  /**
   * Calculate rank from spent XP
   *
   * Uses the RANK_THRESHOLDS constant to determine the appropriate rank
   * based on the character's total spent XP.
   *
   * Edge cases:
   * - Negative XP or XP < 5000: Returns Rank 1
   * - XP >= 30000: Returns Rank 8
   *
   * @param spentXP - The total amount of XP the character has spent
   * @returns The character's rank (1-8)
   */
  static calculateRank(spentXP: number): Rank {
    // Handle negative XP edge case - treat as Rank 1
    if (spentXP < 0) {
      return 1;
    }

    // Find the rank threshold that matches the spent XP
    // RANK_THRESHOLDS is ordered from Rank 1 to Rank 8
    for (const threshold of RANK_THRESHOLDS) {
      if (spentXP >= threshold.minXP && spentXP <= threshold.maxXP) {
        return threshold.rank;
      }
    }

    // Fallback: If XP exceeds all thresholds (shouldn't happen due to Infinity),
    // return Rank 8
    return 8;
  }

  /**
   * Get XP needed to reach the next rank
   *
   * Calculates how much more XP needs to be spent to advance to the next rank.
   * Returns null if the character is already at Rank 8 (maximum rank).
   *
   * Examples:
   * - spentXP = 5000 (Rank 1) → returns 2000 (need 7000 for Rank 2)
   * - spentXP = 6999 (Rank 1) → returns 1 (need 7000 for Rank 2)
   * - spentXP = 7000 (Rank 2) → returns 3000 (need 10000 for Rank 3)
   * - spentXP = 30000 (Rank 8) → returns null
   *
   * @param spentXP - The total amount of XP the character has spent
   * @returns XP needed to reach next rank, or null if at Rank 8
   */
  static getXPToNextRank(spentXP: number): number | null {
    const currentRank = this.calculateRank(spentXP);

    // If at Rank 8, there is no next rank
    if (currentRank === 8) {
      return null;
    }

    // Find the next rank's threshold
    // Since ranks are 1-indexed and array is 0-indexed, next rank threshold is at index currentRank
    const nextRankThreshold = RANK_THRESHOLDS[currentRank];

    // Calculate XP needed to reach the next rank's minimum threshold
    return nextRankThreshold.minXP - spentXP;
  }

  /**
   * Get progress percentage toward next rank
   *
   * Calculates the percentage of progress through the current rank toward the next rank.
   * Returns 100 if the character is at Rank 8 (maximum rank).
   *
   * Calculation:
   * Progress = ((spentXP - currentRankMinXP) / (nextRankMinXP - currentRankMinXP)) * 100
   *
   * Examples:
   * - spentXP = 5000 (Rank 1, min=0, next=7000) → (5000-0)/(7000-0)*100 = 71.4%
   * - spentXP = 7000 (Rank 2, min=7000, next=10000) → (7000-7000)/(10000-7000)*100 = 0%
   * - spentXP = 8500 (Rank 2, min=7000, next=10000) → (8500-7000)/(10000-7000)*100 = 50%
   * - spentXP = 30000 (Rank 8) → returns 100
   *
   * @param spentXP - The total amount of XP the character has spent
   * @returns Percentage progress toward next rank (0-100)
   */
  static getRankProgress(spentXP: number): number {
    const currentRank = this.calculateRank(spentXP);

    // If at Rank 8, return 100 (maximum rank achieved)
    if (currentRank === 8) {
      return 100;
    }

    // Get current rank's threshold (ranks are 1-indexed, array is 0-indexed)
    const currentRankThreshold = RANK_THRESHOLDS[currentRank - 1];
    // Get next rank's threshold
    const nextRankThreshold = RANK_THRESHOLDS[currentRank];

    const currentRankMinXP = currentRankThreshold.minXP;
    const nextRankMinXP = nextRankThreshold.minXP;

    // Calculate progress percentage
    // Handle edge case where spentXP is below current rank's min (e.g., negative XP)
    const effectiveSpentXP = Math.max(spentXP, currentRankMinXP);
    const progress =
      ((effectiveSpentXP - currentRankMinXP) / (nextRankMinXP - currentRankMinXP)) * 100;

    return progress;
  }

  /**
   * Get all ranks available to a character (current and all previous ranks)
   *
   * Returns an array of ranks from 1 up to and including the character's current rank.
   * This is used for advancement filtering - a character can purchase advancements
   * from their current rank AND all previously held ranks.
   *
   * Examples:
   * - spentXP = 5000 (Rank 1) → returns [1]
   * - spentXP = 7000 (Rank 2) → returns [1, 2]
   * - spentXP = 15000 (Rank 4) → returns [1, 2, 3, 4]
   * - spentXP = 30000 (Rank 8) → returns [1, 2, 3, 4, 5, 6, 7, 8]
   *
   * @param spentXP - The total amount of XP the character has spent
   * @returns Array of ranks from 1 to current rank
   */
  static getAvailableRanks(spentXP: number): Rank[] {
    const currentRank = this.calculateRank(spentXP);

    // Build array of ranks from 1 to currentRank
    const ranks: Rank[] = [];
    for (let i = 1; i <= currentRank; i++) {
      ranks.push(i as Rank);
    }

    return ranks;
  }

  /**
   * Check if rank advancement is available
   *
   * Compares the calculated rank (based on spent XP) with the active rank
   * (the rank the player has explicitly selected). Returns true if the
   * calculated rank exceeds the active rank, indicating the player can
   * select a new rank.
   *
   * This is used to detect when a character has accumulated enough XP to
   * advance but hasn't yet selected their next rank.
   *
   * Examples:
   * - spentXP = 7000 (Rank 2), activeRank = 1 → returns true (can advance)
   * - spentXP = 7000 (Rank 2), activeRank = 2 → returns false (already at calculated rank)
   * - spentXP = 15000 (Rank 4), activeRank = 2 → returns true (2 ranks behind)
   * - spentXP = 5000 (Rank 1), activeRank = 1 → returns false (no advancement available)
   *
   * @param spentXP - The total amount of XP the character has spent
   * @param activeRank - The rank the player has explicitly selected
   * @returns true if calculated rank > active rank, false otherwise
   *
   * @see Requirements 1.1, 1.2, 1.3
   */
  static isRankAdvancementAvailable(spentXP: number, activeRank: Rank): boolean {
    const calculatedRank = this.calculateRank(spentXP);
    return calculatedRank > activeRank;
  }

  /**
   * Get the number of pending rank advancements
   *
   * Calculates how many rank levels the character can advance based on their
   * spent XP compared to their currently active rank. This is useful for
   * displaying how many rank selections are pending.
   *
   * The pending count is calculated as (calculated rank - active rank), with
   * a minimum of 0 (no negative pending counts).
   *
   * Examples:
   * - spentXP = 7000 (Rank 2), activeRank = 1 → returns 1 (one rank pending)
   * - spentXP = 15000 (Rank 4), activeRank = 2 → returns 2 (two ranks pending)
   * - spentXP = 5000 (Rank 1), activeRank = 1 → returns 0 (no ranks pending)
   * - spentXP = 7000 (Rank 2), activeRank = 2 → returns 0 (already at calculated rank)
   * - spentXP = 30000 (Rank 8), activeRank = 5 → returns 3 (three ranks pending)
   *
   * @param spentXP - The total amount of XP the character has spent
   * @param activeRank - The rank the player has explicitly selected
   * @returns Number of pending rank advancements (0 or positive)
   *
   * @see Requirements 1.5
   */
  static getPendingRankCount(spentXP: number, activeRank: Rank): number {
    const calculatedRank = this.calculateRank(spentXP);
    const pendingCount = calculatedRank - activeRank;
    return Math.max(0, pendingCount);
  }

  /**
   * Get the next standard career rank number
   *
   * Returns the next rank in the standard career progression. This is used
   * when displaying the standard rank advancement option in the rank selection UI.
   * Returns null if the character is already at Rank 8 (maximum rank).
   *
   * The standard career progression is linear: Rank 1 → Rank 2 → ... → Rank 8.
   * This method simply returns activeRank + 1 if the character is not at maximum rank.
   *
   * Examples:
   * - activeRank = 1 → returns 2
   * - activeRank = 5 → returns 6
   * - activeRank = 7 → returns 8
   * - activeRank = 8 → returns null (at maximum rank)
   *
   * @param activeRank - The character's currently active rank (1-8)
   * @returns The next rank (2-8) or null if at maximum rank
   *
   * @see Requirements 2.1
   */
  static getNextStandardRank(activeRank: Rank): Rank | null {
    // If at maximum rank (8), there is no next standard rank
    if (activeRank >= 8) {
      return null;
    }

    // Return the next rank
    return (activeRank + 1) as Rank;
  }
}
